import { useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { AuthModalWrapper } from "../../Auth/AuthModal";
import {
  bidInterest,
  convertToDecimal,
  getWatermarkedImgixUrl,
} from "services";
import { PDAuctionDetailsHeader } from "./PDAuctionDetailsHeader";
import { PropertyDetailGridView } from "./PropertyDetailGridView";
import ResidentialProperties from "../../home/residentialProperties/ResidentialProperties";
import HowToStartBidding from "../../home/howToStartBidding/HowToStartBidding";
import FAQSection from "../../home/faq/FAQSection";
import { PDAcknowledgmentModal } from "./PDAcknowledgmentModal";
import { PDPropertyDescription } from "./PDPropertyDescription";
import { PDKnowMoreAboutPropertyCardV2 } from "./PDKnowMoreAboutPropertyCardV2";
import { PDMapViewV2 } from "./PDMapViewV2";
import { ImageType, PropertyImagePreview } from "./PropertyImagePreview";
import { usePDAuctionDetails } from "../hooks/usePDAuctionDetails";
import { PDContactAuthorisedOfficer } from "./PDContactAuthorisedOfficer";
import { PDDocumentsView } from "./PDDocumentsView";
import HomeLoanCard from "../../propertyList/HomeLoanCard";
import PDPublicNoticeAggregationDisclaimer from "../PDPublicNoticeAggregationDisclaimer";
import { getSellerTypeDisplay } from "../../common/ShareModal/ShareModal";

export type PropertyType = "auction_property" | "private_property";

interface PropertyDetailWrapperProps {
  propertyData: any;
  defaultLocation: any;
  propertyType: PropertyType;
}

export const PropertyDetailWrapper = ({
  propertyData,
  defaultLocation,
  propertyType,
}: PropertyDetailWrapperProps) => {
  const [authModelOpen, setAuthModelOpen] = useState(false);
  const [acknowledgementOpen, setAcknowledgementOpen] = useState(false);
  const [acknowledgementDetails, setAcknowledgementDetails] = useState({
    name: "",
    mobile: "",
    email: "",
    isFooter: false,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const isLoggedIn = useSelector((state: any) => state.auth.user);
  const { auctionDetails } = usePDAuctionDetails({ propertyData });

  const handleInterested = useCallback(async () => {
    const payload: {
      name: string;
      mobile_number?: string;
      email?: string;
      property_id: string;
    } = {
      name: acknowledgementDetails.name,
      property_id: propertyData.listing_id,
    };

    if (acknowledgementDetails?.mobile?.length === 10) {
      payload.mobile_number = acknowledgementDetails.mobile;
    }

    if (
      acknowledgementDetails.email &&
      acknowledgementDetails?.email?.length > 3
    ) {
      payload.email = acknowledgementDetails.email;
    }

    try {
      await bidInterest(payload);
      setAcknowledgementOpen(true);
      setAcknowledgementDetails({
        name: "",
        mobile: "",
        email: "",
        isFooter: false,
      });
      setErrorMessage("");
    } catch (error) {
      console.error("Error handling bid interest:", error);
      setErrorMessage("Failed to submit interest. Please try again.");
    }
  }, [acknowledgementDetails, propertyData.listing_id]);

  const coordinates = useMemo(() => {
    const locationCoordinates = propertyData?.location_coordinates;
    let _coordinates = {
      isCoordinatesFound: false,
      value: ["na", "na"],
    };
    if (
      locationCoordinates &&
      !locationCoordinates?.toLowerCase()?.includes("na") &&
      !locationCoordinates?.toLowerCase()?.includes("no") &&
      !locationCoordinates?.toLowerCase()?.includes("contact no") &&
      !locationCoordinates?.toLowerCase()?.includes("null")
    ) {
      if (locationCoordinates.includes(",")) {
        const foundCoordinates = locationCoordinates
          .split(",")
          .map((coord: string) =>
            coord
              .toString()
              .replace(/[^\d.-]/g, "")
              .trim(),
          )
          .map((coord: string) => {
            return convertToDecimal(coord.replace(/[nesw]/gi, "").trim());
          });
        const cleanCoords = foundCoordinates?.filter(
          (value: any) => !isNaN(value),
        );

        if (Array?.isArray(cleanCoords) && cleanCoords?.length === 2) {
          _coordinates = {
            isCoordinatesFound: true,
            value: cleanCoords,
          };
        } else {
          _coordinates = {
            isCoordinatesFound: false,
            value: cleanCoords,
          };
        }
      } else if (locationCoordinates && locationCoordinates.includes(" ")) {
        // Handle space-separated coordinates (e.g., 30.89 75.89)
        const foundCoordinates = locationCoordinates
          .split(" ")
          .map((coord: string) =>
            convertToDecimal(
              coord
                .toString()
                .replace(/[^\d.-]/g, "")
                .trim(),
            ),
          );
        const cleanCoords = foundCoordinates?.filter(
          (value: any) => !isNaN(value),
        );
        if (Array?.isArray(cleanCoords) && cleanCoords?.length === 2) {
          _coordinates = {
            isCoordinatesFound: true,
            value: cleanCoords,
          };
        } else {
          _coordinates = {
            isCoordinatesFound: false,
            value: cleanCoords,
          };
        }
      }
    }

    return _coordinates;
  }, [propertyData?.location_coordinates]);

  const propertyDetailsFullLink = useMemo(() => {
    const baseUrl = window.location.href.split("/").filter(Boolean)[1];
    return `https://${baseUrl}/auction-properties/${propertyData?.listing_id}`;
  }, [propertyData?.listing_id]);

  return (
    <div className="bg-brand-rootBackgroundVariant1">
      <div className="space-y-4 2xl:mx-auto px-customPaddingMobile lg:px-customPadding 2xl:px-0 w-full 2xl:w-[1340px]">
        <PDAuctionDetailsHeader
          propertyData={propertyData}
          setAuthModelOpen={setAuthModelOpen}
          setAcknowledgementOpen={setAcknowledgementOpen}
          handleInterested={handleInterested}
          auctionDetails={auctionDetails}
          propertyDetailsFullLink={propertyDetailsFullLink}
          propertyType={propertyType}
        />

        <PropertyDetailGridView
          leftChildren={
            <LeftChildren
              propertyData={propertyData}
              setAuthModelOpen={setAuthModelOpen}
              setAcknowledgementOpen={setAcknowledgementOpen}
              setAcknowledgementDetails={setAcknowledgementDetails}
              propertyType={propertyType}
            />
          }
          rightChildren={
            <RightChildren
              coordinates={coordinates}
              propertyData={propertyData}
            />
          }
        />

        <ResidentialProperties defaultLocation={defaultLocation} />
        <HowToStartBidding />
        <FAQSection />
      </div>

      <AuthModalWrapper
        userLoggedin={isLoggedIn}
        authModelOpen={authModelOpen}
        setAuthModelOpen={setAuthModelOpen}
      />
      <PDAcknowledgmentModal
        acknowledgementOpen={acknowledgementOpen}
        setAcknowledgementOpen={setAcknowledgementOpen}
        errorMessage={errorMessage}
      />
    </div>
  );
};

interface LeftChildrenProps {
  propertyData: any;
  setAuthModelOpen: (value: boolean) => void;
  setAcknowledgementOpen: (value: boolean) => void;
  setAcknowledgementDetails: (value: any) => void;
  propertyType: PropertyType;
}

const LeftChildren = ({
  propertyData,
  setAuthModelOpen,
  setAcknowledgementOpen,
  setAcknowledgementDetails,
  propertyType,
}: LeftChildrenProps) => {
  const details = useMemo(() => {
    const borrowerName = propertyData?.borrower_name || "NA";
    return [
      { label: "Borrower Name", value: borrowerName },
      {
        label: "Type",
        value: `${propertyData?.sub_asset_type?.name}`,
      },
      { label: "Area", value: propertyData?.area || "NA" },
      { label: "Possession", value: propertyData?.possession_type || "NA" },
    ];
  }, [
    propertyData?.area,
    propertyData?.borrower_name,
    propertyData?.possession_type,
    propertyData?.sub_asset_type?.name,
  ]);
  return (
    <>
      <PDPropertyDescription
        description={propertyData?.description}
        details={details}
      />
      <PDDocumentsView
        propertyData={propertyData}
        setAuthModelOpen={setAuthModelOpen}
        propertyType={propertyType}
        title={"Auction Documents"}
      />
      <PDContactAuthorisedOfficer
        propertyData={propertyData}
        setAuthModelOpen={setAuthModelOpen}
        propertyType={propertyType}
        title="View Authorised Officer Details"
      />
      <PDKnowMoreAboutPropertyCardV2
        propertyData={propertyData}
        setAuthModelOpen={setAuthModelOpen}
        setAcknowledgementOpen={setAcknowledgementOpen}
        setAcknowledgementDetails={setAcknowledgementDetails}
      />
    </>
  );
};

interface RightChildrenProps {
  coordinates: {
    isCoordinatesFound: boolean;
    value: string[];
  };
  propertyData: any;
}

const RightChildren = ({ coordinates, propertyData }: RightChildrenProps) => {
  const sliderImages = useMemo<ImageType[]>(() => {
    return (
      propertyData?.images?.map((img: any) => {
        const url = img?.path_to_file;
        const path = new URL(url).pathname.slice(1);

        return {
          filePath: getWatermarkedImgixUrl(
            `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${path}`,
          ),
          fileName: img?.file_name,
        };
      }) || []
    );
  }, [propertyData?.images]);
  return (
    <>
      <PDMapViewV2
        isCoordinateFound={coordinates.isCoordinatesFound}
        position={coordinates.value}
        locality={{
          isFound: propertyData?.locality?.name ? true : false,
          value: propertyData?.locality?.name
            ? propertyData?.locality?.name
            : null,
        }}
        city={propertyData?.city}
        state={propertyData?.state}
        area={propertyData?.area}
      />
      <PropertyImagePreview sliderImages={sliderImages} />
      <PDPublicNoticeAggregationDisclaimer />
      <HomeLoanCard variant="pd" />
    </>
  );
};
